import { useState, useEffect, useRef } from 'react';
import { Layout } from './components/layout';
import { HomePage } from './pages/HomePage';
import { TenBoerPage } from './pages/TenBoerPage';
import { BedumPage } from './pages/BedumPage';
import { ContactPage } from './pages/ContactPage';
import { DraagBij } from './pages/DraagBij';
import { AlgemeneVoorwaarden } from './pages/AlgemeneVoorwaarden';
import { NotFoundPage } from './pages/NotFoundPage';
import { OverOns } from './pages/OverOns';
import './styles/globals.css';

/**
 * Hash-based routing.
 *
 * The hosting (Apache shared with AllowOverride None) ignores .htaccess, so
 * refreshing /bedum returns a hard 404. We move the route into the URL hash:
 *
 *   sportcommunityrestart.nl/#/bedum
 *
 * Apache only ever sees /, which it serves as index.html — the SPA then
 * reads window.location.hash and renders the correct page. Refresh works on
 * every host without any server config.
 *
 * Convention:
 *   #/foo   → route "/foo"
 *   #info   → in-page anchor (left alone, browser handles natively)
 *   ""      → route "/" (home)
 */
const ROUTES = new Set([
  '/',
  '/ten-boer',
  '/bedum',
  '/contact',
  '/draagbij',
  '/algemenevoorwaarden',
  '/over-ons',
]);

const getPathFromHash = (): string => {
  const h = window.location.hash || '';
  if (h.startsWith('#/')) return h.slice(1) || '/';
  return '/';
};

export default function App() {
  const [currentPage, setCurrentPage] = useState<string>(
    typeof window !== 'undefined' ? getPathFromHash() : '/'
  );
  const currentRef = useRef(currentPage);
  const [fade, setFade] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Migrate legacy /bedum-style URLs (and the 404.html sessionStorage
    // fallback) into hash form so they keep working.
    try {
      const stored = sessionStorage.getItem('spa_redirect');
      if (stored) {
        sessionStorage.removeItem('spa_redirect');
        if (stored !== '/' && !window.location.hash) {
          window.location.replace('/#' + stored);
          return;
        }
      }
    } catch {}

    if (window.location.pathname !== '/' && !window.location.hash) {
      // Refresh on a path Apache served 404 for would never reach this code,
      // but if it ever does (e.g. on hosts that fall through), reroute via hash.
      const target = '/#' + window.location.pathname + window.location.search;
      window.history.replaceState({}, '', target);
    }

    const initial = getPathFromHash();
    setCurrentPage(initial);
    currentRef.current = initial;

    const onHashChange = () => {
      const next = getPathFromHash();
      // Ignore in-page anchor changes (e.g. "#info") that don't change the route
      if (next === currentRef.current) return;
      currentRef.current = next;
      setFade(false);
      window.setTimeout(() => {
        setCurrentPage(next);
        setFade(true);
      }, 50);
    };
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('popstate', onHashChange);
    };
  }, []);

  /* Scroll to top on page change */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentPage]);

  /* Intercept internal-link clicks and route them through the hash */
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      // Respect modifier-clicks (open in new tab etc.)
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const target = e.target as HTMLElement;
      const link = target?.closest('a');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('/')) return;
      // Honor target="_blank" / download
      if (link.target === '_blank' || link.hasAttribute('download')) return;

      e.preventDefault();
      setLoading(true);
      setFade(false);

      const newHash = '#' + href;
      window.setTimeout(() => {
        if (window.location.hash === newHash) {
          // Same route — still need to trigger render cycle
          setCurrentPage(href);
          setFade(true);
          window.setTimeout(() => setLoading(false), 250);
        } else {
          // Setting hash will fire hashchange which runs the cross-fade
          window.location.hash = href;
          window.setTimeout(() => setLoading(false), 400);
        }
      }, 50);
    };
    document.addEventListener('click', handleLinkClick);
    return () => document.removeEventListener('click', handleLinkClick);
  }, []);

  const renderPage = () => {
    const route = ROUTES.has(currentPage) ? currentPage : '/__notfound';
    switch (route) {
      case '/':
        return <HomePage />;
      case '/ten-boer':
        return <TenBoerPage />;
      case '/bedum':
        return <BedumPage />;
      case '/contact':
        return <ContactPage />;
      case '/draagbij':
        return <DraagBij />;
      case '/algemenevoorwaarden':
        return <AlgemeneVoorwaarden />;
      case '/over-ons':
        return <OverOns />;
      default:
        return <NotFoundPage />;
    }
  };

  return (
    <Layout>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-[9999] animate-fade-in">
          <div className="w-16 h-16 border-4 border-[#0F4DF9] border-t-[#F5B800] rounded-full animate-spin"></div>
        </div>
      )}
      <div
        className={`transition-opacity duration-500 ${fade ? 'opacity-100' : 'opacity-0'}`}
        style={{ minHeight: '100vh' }}
      >
        {renderPage()}
      </div>
    </Layout>
  );
}
